import { useLayoutEffect, useRef, useState } from 'react'

import { RectI } from '../../types/types'

export interface UseContainerRelativePositionResultIProps {
  anchorRef: React.RefObject<HTMLDivElement>
  left: number
  top: number
}

/**
 * Converts a marker's viewport rect (from `getBoundingClientRect`, via
 * `getDeviceClientRect` / `getTailgateClientRect`) into coordinates relative to
 * the anchor's nearest positioned ancestor — which is what an absolutely
 * positioned element's `left`/`top` are measured against.
 *
 * This replaces the old fixed `-60` / `-65` offsets, which hardcoded the
 * assumption that the ancestor sat at viewport (60, 65) — i.e. behind a
 * collapsed 60px side nav. Any other side-nav width (expanded 252px, or a
 * future value) left the card mis-anchored by the difference. Measuring the
 * ancestor at layout time keeps the card on its marker regardless of side-nav
 * width, and also tracks fullscreen, where the positioned ancestor changes.
 *
 * Shared by both popover variants so their anchoring can't drift apart.
 */
export const useContainerRelativePosition = ({
  x,
  y,
}: RectI): UseContainerRelativePositionResultIProps => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x, top: y })

  useLayoutEffect(() => {
    // offsetParent is the nearest positioned ancestor — the same box the Box's
    // absolute left/top resolve against. Null (e.g. display:none) falls back to
    // the viewport origin, matching the raw client rect.
    const parent = anchorRef.current?.offsetParent as HTMLElement | null
    const rect = parent?.getBoundingClientRect()
    setPos({ left: x - (rect?.left ?? 0), top: y - (rect?.top ?? 0) })
  }, [x, y])

  return { anchorRef, ...pos }
}
